import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Calendar, ArrowLeft, Tag } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";

export default function BlogPost() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch((err) => { if (err?.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    // Update SEO meta on the fly for this post
    document.title = `${post.title} · Trustline Services NL`;
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content || "");
    };
    setMeta("description", post.meta_description || post.excerpt || "");
    return () => { document.title = "Trustline Services — Reliable Cleaning Solutions"; };
  }, [post]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="h-8 w-2/3 bg-slate-100 rounded animate-pulse" />
        <div className="mt-4 h-4 w-1/3 bg-slate-100 rounded animate-pulse" />
        <div className="mt-10 space-y-3">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-4 bg-slate-100 rounded animate-pulse" style={{ width: `${80 + Math.random() * 20}%` }} />)}
        </div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-24 text-center">
        <h1 className="font-heading text-3xl font-bold text-[#1B3A6B]">{t("blog_page.not_found_title")}</h1>
        <p className="mt-3 text-slate-500">{t("blog_page.not_found_subtitle")}</p>
        <Button onClick={() => nav("/blog")} className="mt-6 bg-[#1B3A6B] hover:bg-[#152D54] text-white rounded-full">
          <ArrowLeft className="w-4 h-4 mr-1" /> {t("blog_page.back_to_blog")}
        </Button>
      </div>
    );
  }

  let dateText = "";
  try { dateText = format(new Date(post.published_at), "MMMM d, yyyy"); } catch { /* ignore */ }

  return (
    <article className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-20" data-testid={`blog-post-${post.slug}`}>
      <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#1B3A6B]" data-testid="blog-back-link">
        <ArrowLeft className="w-4 h-4" /> {t("blog_page.back_to_blog")}
      </Link>

      {post.tags && post.tags.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-1.5">
          {post.tags.map((tag, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide font-semibold text-[#3F7A1F] bg-[#E8F5E0] px-2.5 py-1 rounded-full">
              <Tag className="w-3 h-3" />{tag}
            </span>
          ))}
        </div>
      )}

      <h1 className="mt-3 font-heading text-3xl md:text-5xl font-extrabold text-[#0F172A] leading-tight tracking-tight">{post.title}</h1>
      <div className="mt-4 flex items-center gap-4 text-sm text-slate-500">
        {post.author && <span>{post.author}</span>}
        {dateText && <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {dateText}</span>}
      </div>

      {post.cover_image && (
        <img src={post.cover_image} alt={post.title} className="mt-8 w-full aspect-[16/9] object-cover rounded-2xl ring-1 ring-slate-200" />
      )}

      <div
        className="mt-10 prose prose-slate max-w-none prose-headings:font-heading prose-headings:text-[#1B3A6B] prose-a:text-[#5BA83F] prose-img:rounded-xl prose-strong:text-[#0F172A]"
        // RankPill payload is trusted via HMAC-signed webhook on the server
        dangerouslySetInnerHTML={{ __html: post.content }}
      />

      <div className="mt-16 pt-8 border-t border-slate-200 text-center">
        <h3 className="font-heading text-xl font-bold text-[#1B3A6B]">{t("blog_page.cta_title")}</h3>
        <p className="mt-2 text-sm text-slate-500">{t("blog_page.cta_subtitle")}</p>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <Link to="/book"><Button className="bg-[#5BA83F] hover:bg-[#4A8A33] text-white rounded-full px-6" data-testid="blog-cta-book">{t("blog_page.cta_book")}</Button></Link>
          <Link to="/quote"><Button variant="outline" className="rounded-full px-6" data-testid="blog-cta-quote">{t("blog_page.cta_quote")}</Button></Link>
        </div>
      </div>
    </article>
  );
}
